import React, { Suspense } from "react";
import { Navigate, Route, Routes, Outlet } from "react-router-dom";
import { Spinner } from "@shared/components/ui";
import { AuthTransferPage } from "@shared/components/AuthTransferPage";
import { MobileOnlyGate } from "@shared/components/MobileOnlyGate";
import { RequireAuth } from "@shared/components/RequireAuth";
import { MobileShell } from "./layouts/MobileShell";

// Lazy-loaded pages
const DashboardPage = React.lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const MessagesPage = React.lazy(() => import("./pages/MessagesPage").then(m => ({ default: m.MessagesPage })));
const BillingPage = React.lazy(() => import("./pages/BillingPage").then(m => ({ default: m.BillingPage })));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const IncidentListPage = React.lazy(() => import("./pages/IncidentListPage").then(m => ({ default: m.IncidentListPage })));
const IncidentDetailPage = React.lazy(() => import("./pages/IncidentDetailPage").then(m => ({ default: m.IncidentDetailPage })));
const LinkPage = React.lazy(() => import("./pages/LinkPage").then(m => ({ default: m.LinkPage })));
const ChildDetailPage = React.lazy(() => import("./pages/ChildDetailPage").then(m => ({ default: m.ChildDetailPage })));
const AttendanceHistoryPage = React.lazy(() => import("./pages/AttendanceHistoryPage").then(m => ({ default: m.AttendanceHistoryPage })));
const ActivityHistoryPage = React.lazy(() => import("./pages/ActivityHistoryPage").then(m => ({ default: m.ActivityHistoryPage })));

// Legal pages - lazy loaded (public, accessible pre-login)
const TermsPage = React.lazy(() => import("@shared/components/legal/TermsContent").then(m => ({ default: m.TermsContent })));
const PrivacyPage = React.lazy(() => import("@shared/components/legal/PrivacyContent").then(m => ({ default: m.PrivacyContent })));

// Auth pages (loaded synchronously)
import { LoginPage } from "./pages/LoginPage";

function PageLoader() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <Spinner label="Loading..." />
    </div>
  );
}

function MobileOnlyGateLayout() {
  return (
    <MobileOnlyGate>
      <Outlet />
    </MobileOnlyGate>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/auth-transfer" element={<AuthTransferPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/link" element={<Suspense fallback={<PageLoader />}><LinkPage /></Suspense>} />
      <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />

      <Route element={<RequireAuth />}>
        <Route element={<MobileOnlyGateLayout />}>
          <Route element={<MobileShell />}>
            <Route path="/" element={<Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>} />
            <Route path="/messages" element={<Suspense fallback={<PageLoader />}><MessagesPage /></Suspense>} />
            <Route path="/billing" element={<Suspense fallback={<PageLoader />}><BillingPage /></Suspense>} />
            <Route path="/billing/:id" element={<Suspense fallback={<PageLoader />}><BillingPage /></Suspense>} />
            <Route path="/incidents" element={<Suspense fallback={<PageLoader />}><IncidentListPage /></Suspense>} />
            <Route path="/incidents/:id" element={<Suspense fallback={<PageLoader />}><IncidentDetailPage /></Suspense>} />
            <Route path="/child/:id" element={<Suspense fallback={<PageLoader />}><ChildDetailPage /></Suspense>} />
            <Route path="/child/:id/attendance" element={<Suspense fallback={<PageLoader />}><AttendanceHistoryPage /></Suspense>} />
            <Route path="/child/:id/activities" element={<Suspense fallback={<PageLoader />}><ActivityHistoryPage /></Suspense>} />
            <Route path="/me" element={<Suspense fallback={<PageLoader />}><ProfilePage /></Suspense>} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
